import useLeverage from '@/hooks/use-leverage';
import useSymbol from '@/hooks/use-symbol';
import { getCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { OrderType } from '@/types';
import { formatNumber, parseDecimalToBigInt } from '@/utils/value-format';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useStore } from 'zustand';
import { getEstLiqPrice } from '../../utils/math';
import { OrderFormSchema } from '../order-form';

type Props = {
  scaleOrders: OrderFormSchema[];
};

export default function EstLiqPrice({ scaleOrders }: Props) {
  const symbol = useSymbol();

  const form = useFormContext<OrderFormSchema>();

  const priceDecimals = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.priceDecimals,
  );

  const leverage = useLeverage(symbol);

  const isBuy = form.watch('order.isBuy');
  const orderType = form.watch('order.orderType');
  const limitPrice = form.watch('order.limitPrice');
  const size = form.watch('order.size');

  const estLiqPrice = useMemo(() => {
    const account = getCurrentAccount();
    const { marketSpec } = useMarketStore(symbol).getState();

    let price: bigint | undefined;

    switch (orderType) {
      case OrderType.LIMIT:
        price = parseDecimalToBigInt(limitPrice, marketSpec.priceDecimals);
        break;
      case OrderType.SCALE:
        const avgEntryPrice: number =
          orderType === OrderType.SCALE && scaleOrders.length
            ? scaleOrders.reduce(
                (prev, curr) =>
                  prev + +curr.order.limitPrice * +curr.order.size,
                0,
              ) / scaleOrders.reduce((prev, curr) => prev + +curr.order.size, 0)
            : 0;
        price = parseDecimalToBigInt(
          avgEntryPrice.toString(),
          marketSpec.priceDecimals,
        );
        break;
    }

    const initMarginRatio = 10n ** 4n / BigInt(leverage);

    return getEstLiqPrice(
      symbol,
      isBuy,
      parseDecimalToBigInt(size, marketSpec.sizeDecimals),
      account,
      initMarginRatio,
      price,
    );
  }, [symbol, size, isBuy, leverage, limitPrice, scaleOrders]);

  if (!estLiqPrice?.bigint)
    return <span className="text-muted-foreground">N/A</span>;

  return (
    <span className="text-red">
      {formatNumber(estLiqPrice?.decimal, {
        digits: Number(priceDecimals),
      })}
    </span>
  );
}
